/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { PriceConfig } from '@truckstop-tms/zuum-lib';

/**
 * Name of all the pricing configurations as seen here:
 * https://zuumapp.atlassian.net/wiki/spaces/ZUUM/pages/164135059/SA+Configuration#Price%2FRate-Tab
 */
export enum PRICE_CONFIGURATION_NAMES {
  // Waiver
  WAIVE_PLATFORM_ACCESS_FEE = 'WAIVE_PLATFORM_ACCESS_FEE',
  WAIVE_SHIPMENT_COST_FEE = 'WAIVE_SHIPMENT_COST_FEE',

  // Platform Access
  PLATFORM_ACCESS_PER_SEAT = 'WAIVE_PLATFORM_ACCESS_FEE',

  // Shipments
  LOAD_START = 'LOAD_START',
  NO_STANDARD_LOADS = 'NO_STANDARD_LOADS',
  COST_PER_SHIPMENT = 'COST_PER_SHIPMENT',
  COST_AFTER_STANDARD = 'COST_AFTER_STANDARD',

  // User related
  USER_COST = 'USER_COST',

  // Add on
  ZUUM_TEXT_ADD_ON = 'ZUUM_TEXT_ADD_ON',
  DIRECT_EMAIL_ADD_ON = 'DIRECT_EMAIL_ADD_ON',
  DIALPAD_ADD_ON = 'DIALPAD_ADD_ON',
  BUNDLE_ADD_ON = 'BUNDLE_ADD_ON',
  CARRIER_NAME_ADD_ON = 'CARRIER_NAME_ADD_ON',
  APPLY_FOR_CREDIT = 'APPLY_FOR_CREDIT',

  // Referral
  TOTAL_REFERRALS_LIMIT = 'TOTAL_REFERRALS_LIMIT',
  DISCOUNT_RATE_MAX_REFERRAL = 'DISCOUNT_RATE_MAX_REFERRAL',
  EXPIRE_BASIS_REFERRAL = 'EXPIRE_BASIS_REFERRAL',

  // Project Type
  UI_UX_DESIGN = 'UI_UX_DESIGN',
  DEVELOPMENT = 'DEVELOPMENT',
  INTEGRATION = 'INTEGRATION',
  CHANGE_ORDER = 'CHANGE_ORDER',
  SETUP = 'SETUP',
  TRAINING = 'TRAINING',
  MAINTENANCE = 'MAINTENANCE',

  // Saas Invoice
  SAAS_INVOICE_PAYMENT_TERMS = 'SAAS_INVOICE_PAYMENT_TERMS',
  SAAS_INVOICE_SPLIT_CHARGES = 'SAAS_INVOICE_SPLIT_CHARGES',

  // Custom Invoice
  CUSTOM_INVOICE_PAYMENT_TERMS = 'CUSTOM_INVOICE_PAYMENT_TERMS',

  // Platform Access
  MONTHLY_PER_SEAT = 'MONTHLY_PER_SEAT',
  YEARLY_PER_SEAT = 'YEARLY_PER_SEAT',

  // Shipments
  MIN_NO_SHIPMENTS_MONTH = 'MIN_NO_SHIPMENTS_MONTH',
  MONTHLY_COST_SHIPMENT = 'MONTHLY_COST_SHIPMENT',
  MONTHLY_OVERAGE_COST_SHIPMENT = 'MONTHLY_OVERAGE_COST_SHIPMENT',
  YEARLY_COST_SHIPMENT = 'YEARLY_COST_SHIPMENT',
  YEARLY_OVERAGE_COST_SHIPMENT = 'YEARLY_OVERAGE_COST_SHIPMENT',

  // Features
  DYNAMIC_SMART_RATE = 'DYNAMIC_SMART_RATE',
  DYNAMIC_RFP = 'DYNAMIC_RFP',
  LANE_SEEKER = 'LANE_SEEKER',
  MARKETPLACE = 'MARKETPLACE',

  // Custom
  CUSTOM_NAME = 'CUSTOM_NAME',

  // Requirements Setting
  GENERAL_INSURANCE = 'GENERAL_INSURANCE',
  GENERAL_INSURANCE_FINANCIALS = 'GENERAL_INSURANCE_FINANCIALS',
  AUTO_INSURANCE = 'AUTO_INSURANCE',
  AUTO_INSURANCE_FINANCIALS = 'AUTO_INSURANCE_FINANCIALS',
  CARGO_INSURANCE = 'CARGO_INSURANCE',
  CARGO_INSURANCE_FINANCIALS = 'CARGO_INSURANCE_FINANCIALS',

  MC_DOT_CHECKED = 'MC_DOT_CHECKED',
  DOCUMENTS_CHECKED = 'DOCUMENTS_CHECKED',
  OK_TO_LOAD = 'OK_TO_LOAD',

  DRIVER_CURRENT_LOCATION = 'DRIVER_CURRENT_LOCATION',
  DRIVER_ESTIMATED_ARRIVAL = 'DRIVER_ESTIMATED_ARRIVAL',
  TRAILER_NO = 'TRAILER_NO',
  CARRIER_PRICE = 'CARRIER_PRICE',

  FOUR_CHECK_CALLS = 'FOUR_CHECK_CALLS',
  FOUR_CHECK_CALLS_BANYAN = 'FOUR_CHECK_CALLS_BANYAN',

  LOCATION_MUST_HAVE_NAMES = 'LOCATION_MUST_HAVE_NAMES',

  ASSIGN_SALES_PERSON_SHIPMENT = 'ASSIGN_SALES_PERSON_SHIPMENT',

  MUST_DEFINE_AWARD_TYPE = 'DEFINE_AWARD_TYPE',

  MUST_ENTER_COMPANY_INFO = 'MUST_ENTER_COMPANY_INFO',
  ASSIGN_LEAD_GENERATOR = 'ASSIGN_LEAD_GENERATOR',
  ASSIGN_PRICING_PERSON = 'ASSIGN_PRICING_PERSON',
  MUST_ENTER_CONTACT_INFO = 'MUST_ENTER_CONTACT_INFO',
  MUST_ENTER_LAST_NAME = 'MUST_ENTER_LAST_NAME',
  MUST_ENTER_EMAIL = 'MUST_ENTER_EMAIL',
  MUST_ENTER_ADDRESSES = 'MUST_ENTER_ADDRESSES',
  MUST_ENTER_PAYMENT_TERMS = 'MUST_ENTER_PAYMENT_TERMS',

  QUICKBOOKS_SEND_INVOICE_BEFORE_EXPORT = 'QUICKBOOKS_SEND_INVOICE_BEFORE_EXPORT',
}

/**
 * Description of all the pricing configurations as seen here:
 * https://zuumapp.atlassian.net/wiki/spaces/ZUUM/pages/164135059/SA+Configuration#Price%2FRate-Tab
 */
export enum PRICE_CONFIGURATION_DESCRIPTION {
  // Waiver
  WAIVE_PLATFORM_ACCESS_FEE = 'Waive platform access fee',
  WAIVE_SHIPMENT_COST_FEE = 'Waive shipment cost fee',

  // Platform Access
  PLATFORM_ACCESS_PER_SEAT = 'Per seat (user)',

  // Shipment related
  NO_STANDARD_LOADS = 'Standard number of shipments per month',
  COST_PER_SHIPMENT = 'Cost per Shipment',
  COST_AFTER_STANDARD = 'Overage Cost per Shipment',

  // User related
  USER_COST = 'Each user subscription costs',

  // Add on
  ZUUM_TEXT_ADD_ON = 'Truckstop TMS Text (Unlimited)',
  DIRECT_EMAIL_ADD_ON = 'Direct Email',
  DIALPAD_ADD_ON = 'Dialpad',
  BUNDLE_ADD_ON = 'Bundle',
  CARRIER_NAME_ADD_ON = 'Carrier Name',
  APPLY_FOR_CREDIT = 'Apply For Credit',

  // Referral
  TOTAL_REFERRALS_LIMIT = 'Total number of referrals you can invite',
  DISCOUNT_RATE_MAX_REFERRAL = 'The discount rate you can get from the max referral',
  EXPIRE_BASIS_REFERRAL = 'Referral discount expires on what basis',

  // Project Type
  UI_UX_DESIGN = 'UI/UX Design ',
  DEVELOPMENT = 'Development',
  INTEGRATION = 'Integration',
  CHANGE_ORDER = 'Change Order',
  SETUP = 'Setup',
  TRAINING = 'Training',
  MAINTENANCE = 'Maintenance',

  // Saas Invoice
  SAAS_INVOICE_PAYMENT_TERMS = 'Payment Terms',
  SAAS_INVOICE_SPLIT_CHARGES = 'Split SaaS Charges',

  // Custom Invoice
  CUSTOM_INVOICE_PAYMENT_TERMS = 'Payment Terms',

  // Platform Access
  MONTHLY_PER_SEAT = '[MONTHLY] Per seat (user)',
  YEARLY_PER_SEAT = '[YEARLY] Per seat (user)',

  // Shipments
  MIN_NO_SHIPMENTS_MONTH = 'Minimum number of shipments per month',
  MONTHLY_COST_SHIPMENT = '[MONTHLY] Cost per Shipment',
  MONTHLY_OVERAGE_COST_SHIPMENT = '[MONTHLY] Overage Cost per Shipment',
  YEARLY_COST_SHIPMENT = '[YEARLY] Cost per Shipment',
  YEARLY_OVERAGE_COST_SHIPMENT = '[YEARLY] Overage Cost per Shipment',

  // Features
  DYNAMIC_SMART_RATE = 'Dynamic Smart Rate',
  DYNAMIC_RFP = 'Dynamic RFP',
  LANE_SEEKER = 'Lane Seeker',
  MARKETPLACE = 'Marketplace',

  // Requirements Setting
  GENERAL_INSURANCE = 'Carrier General Insurance Required',
  GENERAL_INSURANCE_FINANCIALS = 'When entering General insurance information, the user must add the insurance financials',
  AUTO_INSURANCE = 'Carrier Auto Insurance Required',
  AUTO_INSURANCE_FINANCIALS = 'When entering Auto insurance information, the user must add the insurance financials',
  CARGO_INSURANCE = 'Carrier Cargo Insurance Required',
  CARGO_INSURANCE_FINANCIALS = 'When entering Cargo insurance information, the user must add the insurance financials',

  MC_DOT_CHECKED = `When adding/inviting a carrier, the user must confirm that the carrier's MC/DOT # has been checked`,
  DOCUMENTS_CHECKED = `When adding a carrier, the user must confirm that the carrier's documents have been checked`,
  OK_TO_LOAD = 'Enable Ok to Load / Do Not Use (when disabled, you will be able to tender loads to all carriers in your database without restriction) ',

  DRIVER_CURRENT_LOCATION = 'Driver current location',
  DRIVER_ESTIMATED_ARRIVAL = 'Driver estimated time of arrival ',
  TRAILER_NO = 'Trailer #',
  CARRIER_PRICE = 'Carrier price',

  FOUR_CHECK_CALLS = ' At least 4 check calls must be logged on a shipment before the shipment can be moved to an "Accounting" status ',
  FOUR_CHECK_CALLS_BANYAN = 'Banyan LTL Integration - At least 4 check calls must be logged on an LTL shipment before the shipment can be moved to an “Accounting” status',

  LOCATION_MUST_HAVE_NAMES = 'Locations (facilities) must have names',

  ASSIGN_SALES_PERSON_SHIPMENT = 'When building a new shipment, the user must assign a Sales person to the shipment.',

  MUST_DEFINE_AWARD_TYPE = 'When building a new shipment, the user must define an award type (spot, contract, etc.) for the shipment.',

  MUST_ENTER_COMPANY_INFO = 'When adding a new Customer, the user must enter the company information',
  ASSIGN_LEAD_GENERATOR = 'From company information, the user must assign a Sales Person to the Customer.',
  ASSIGN_PRICING_PERSON = 'From company information, the user must assign a Pricing person to the Customer.',
  MUST_ENTER_CONTACT_INFO = 'When adding a new Customer, the user must enter the contact information',
  MUST_ENTER_LAST_NAME = 'From contact information, the user must enter the last name of the contact person',
  MUST_ENTER_EMAIL = 'From contact information, the user must enter email address of the contact person',
  MUST_ENTER_ADDRESSES = 'When adding a new Customer, the user must enter the address(es)',
  MUST_ENTER_PAYMENT_TERMS = 'When adding a new Customer, the user must enter payment terms',

  QUICKBOOKS_SEND_INVOICE_BEFORE_EXPORT = 'Invoice must be emailed to customer before it can be exported to Quickbooks',
  NON_MANAGER_EDITING_LOADS = 'Prevent non-managers from editing loads hours after drop-date. (720 hours max)',
}

/**
 * Defaut values of all the pricing configurations as seen here:
 * https://zuumapp.atlassian.net/wiki/spaces/ZUUM/pages/164135059/SA+Configuration#Price%2FRate-Tab
 */
export enum PRICE_CONFIGURATION_DEFAULT_VALUES {
  // Waiver
  WAIVE_PLATFORM_ACCESS_FEE = '0',
  WAIVE_SHIPMENT_COST_FEE = '0',

  // Platform Access
  PLATFORM_ACCESS_PER_SEAT = '0',

  // Shipment related
  NO_STANDARD_LOADS = '30',
  COST_PER_SHIPMENT = '7',
  COST_AFTER_STANDARD = '4',

  // User related
  USER_COST = '4',

  // Add on
  ZUUM_TEXT_ADD_ON = '49.99',
  DIRECT_EMAIL_ADD_ON = '49.99',
  DIALPAD_ADD_ON = '49.99',
  BUNDLE_ADD_ON = '120.00',

  // Referral
  TOTAL_REFERRALS_LIMIT = '10',
  DISCOUNT_RATE_MAX_REFERRAL = '10',
  EXPIRE_BASIS_REFERRAL = 'Monthly',

  // Project Type
  UI_UX_DESIGN = '150',
  DEVELOPMENT = '200',
  INTEGRATION = '200',
  CHANGE_ORDER = '500',
  SETUP = '200',
  TRAINING = '200',
  MAINTENANCE = '200',

  // Saas Invoice
  SAAS_INVOICE_PAYMENT_TERMS = 'Net 15',

  // Custom Invoice
  CUSTOM_INVOICE_PAYMENT_TERMS = 'Net 30',
}

/**
 * Type of all the pricing configurations as seen here:
 * https://zuumapp.atlassian.net/wiki/spaces/ZUUM/pages/164135059/SA+Configuration#Price%2FRate-Tab
 */
export enum PRICE_CONFIGURATION_TYPE {
  // User related
  USER_COST = 'USERS',

  // Shipment related
  NO_STANDARD_LOADS = 'SHIPMENT',
  COST_AFTER_STANDARD = 'SHIPMENT',
  MIN_NO_SHIPMENTS_MONTH = 'SHIPMENT',
  MONTHLY_COST_SHIPMENT = 'SHIPMENT',
  MONTHLY_OVERAGE_COST_SHIPMENT = 'SHIPMENT',
  YEARLY_COST_SHIPMENT = 'SHIPMENT',
  YEARLY_OVERAGE_COST_SHIPMENT = 'SHIPMENT',

  // Add on
  ZUUM_TEXT_ADD_ON = 'ADD-ON',
  DIRECT_EMAIL_ADD_ON = 'ADD-ON',
  DIALPAD_ADD_ON = 'ADD-ON',
  BUNDLE_ADD_ON = 'ADD-ON',
  CARRIER_NAME_ADD_ON = 'ADD-ON',
  APPLY_FOR_CREDIT = 'ADD-ON',

  // Referral
  TOTAL_REFERRALS_LIMIT = 'REFERRAL',
  DISCOUNT_RATE_MAX_REFERRAL = 'REFERRAL',
  EXPIRE_BASIS_REFERRAL = 'REFERRAL',

  // Project Type
  UI_UX_DESIGN = 'PROJECT-TYPE',
  DEVELOPMENT = 'PROJECT-TYPE',
  INTEGRATION = 'PROJECT-TYPE',
  CHANGE_ORDER = 'PROJECT-TYPE',
  SETUP = 'PROJECT-TYPE',
  TRAINING = 'PROJECT-TYPE',
  MAINTENANCE = 'PROJECT-TYPE',

  // Saas Invoice
  SAAS_INVOICE_PAYMENT_TERMS = 'SAAS-INVOICE-PAYMENT-TERMS',
  SAAS_INVOICE_SPLIT_CHARGES = 'SAAS-INVOICE-PAYMENT-TERMS',

  // Custom Invoice
  CUSTOM_INVOICE_PAYMENT_TERMS = 'CUSTOM-INVOICE-PAYMENT-TERMS',

  // Waiver related
  WAIVE_PLATFORM_ACCESS_FEE = 'WAIVER',
  WAIVE_SHIPMENT_COST_FEE = 'WAIVER',

  // Platform Access
  PER_SEAT = 'PLATFORM-ACCESS',

  // Features
  DYNAMIC_SMART_RATE = 'FEATURE',
  DYNAMIC_RFP = 'FEATURE',
  LANE_SEEKER = 'FEATURE',
  MARKETPLACE = 'FEATURE',

  // Requirements Setting
  GENERAL_INSURANCE = 'INSURANCE',
  GENERAL_INSURANCE_FINANCIALS = 'INSURANCE',
  AUTO_INSURANCE = 'INSURANCE',
  AUTO_INSURANCE_FINANCIALS = 'INSURANCE',
  CARGO_INSURANCE = 'INSURANCE',
  CARGO_INSURANCE_FINANCIALS = 'INSURANCE',

  MC_DOT_CHECKED = 'CARRIER_VALIDATION_OK_TO_LOAD',
  DOCUMENTS_CHECKED = 'CARRIER_VALIDATION_OK_TO_LOAD',
  OK_TO_LOAD = 'CARRIER_VALIDATION_OK_TO_LOAD',

  DRIVER_CURRENT_LOCATION = 'CARRIER_TENDER_OPTIONS',
  DRIVER_ESTIMATED_ARRIVAL = 'CARRIER_TENDER_OPTIONS',
  TRAILER_NO = 'CARRIER_TENDER_OPTIONS',
  CARRIER_PRICE = 'CARRIER_TENDER_OPTIONS',

  FOUR_CHECK_CALLS = 'UPDATING_STATUSES_WITHOUT_CHECK_CALLS',
  FOUR_CHECK_CALLS_BANYAN = 'UPDATING_STATUSES_WITHOUT_CHECK_CALLS',

  LOCATION_MUST_HAVE_NAMES = 'LOCATION_NAME',

  ASSIGN_SALES_PERSON_SHIPMENT = 'SALES_PERSON_REQUIRED',

  MUST_DEFINE_AWARD_TYPE = 'AWARD_TYPE_REQUIRED',

  MUST_ENTER_COMPANY_INFO = 'CUSTOMER_INFO_REQUIRED',
  ASSIGN_LEAD_GENERATOR = 'CUSTOMER_INFO_REQUIRED',
  ASSIGN_PRICING_PERSON = 'CUSTOMER_INFO_REQUIRED',
  MUST_ENTER_CONTACT_INFO = 'CUSTOMER_INFO_REQUIRED',
  MUST_ENTER_LAST_NAME = 'CUSTOMER_INFO_REQUIRED',
  MUST_ENTER_EMAIL = 'CUSTOMER_INFO_REQUIRED',
  MUST_ENTER_ADDRESSES = 'CUSTOMER_INFO_REQUIRED',
  MUST_ENTER_PAYMENT_TERMS = 'CUSTOMER_INFO_REQUIRED',

  QUICKBOOKS_SEND_INVOICE_BEFORE_EXPORT = 'QUICKBOOK_INTEGRATION',

  COMPANY_CONFIG = 'COMPANY_CONFIG',

  EQUIPMENT_TYPE = 'EQUIPMENT_TYPE',

  SHIPMENT_OPTIONS = 'SHIPMENT_OPTIONS',

  CUSTOMERS_FOR_SHIPMENTS = 'CUSTOMERS_FOR_SHIPMENTS',

  ACCOUNTING = 'ACCOUNTING',
}

/**
 * Type of all the pricing configurations as seen here:
 * https://zuumapp.atlassian.net/wiki/spaces/ZUUM/pages/164135059/SA+Configuration#Price%2FRate-Tab
 */
export enum PRICE_CONFIGURATION_VALUE_TYPE {
  CURRENCY = '$',
  UNIT = 'Unit',
  PERCENTAGE = '%',
  PERIOD = 'Period',
  PER_HOUR = 'per hour',
  BOOLEAN = 'Boolean',
}

export enum PRICE_CONFIGURATION_CATEGORY {
  PLAN_PRICE = 'PLAN_PRICE',
  INVOICE = 'INVOICE',
  FEATURE_ACCESIBILITY = 'FEATURE_ACCESIBILITY',
  ZUUM_STORE = 'ZUUM_STORE',
  TRUCKSTOP_STORE = 'TRUCKSTOP_STORE',
  PROJECT_TYPE = 'PROJECT_TYPE',
  REFERRAL = 'REFERRAL',
  REQUIREMENTS_SETTING = 'REQUIREMENTS_SETTING',
  COMPANY_CONFIG = 'COMPANY_CONFIG',
}

export const ADD_ON_SORT = [
  'APPLY_FOR_CREDIT',
  'QUICKBOOK',
  'NET_SUITE',
  'EDI_CHECK_CALL',
  'DAT',
  'GREENSCREENS',
  'TRUCK_STOP',
  'TRUCKER_TOOLS',
  'TBC_INTEGRATION',
  'LTL',
  'MACROPOINT',
  'MY_CARRIER_PORTAL',
  'HIGHWAY',
  'SAMSARA',
  'Z_SCAN_W9',
  'Z_SCAN_INSURANCE',
  'DWOLLA',
  'ZUUM_PAY_CHECKS',
  'ZUUM_SIGN_CARRIER_RATE_CONFIRMATION',
  'ZUUM_SIGN_CARRIER_ONBOARDING',
  'ZUUM_TEXT',
  'SLA_SUPPORT',
  'ZUUM_SIGN_W9',
  'TRUCKSTOP_TMS_TEXT',
  'OTTLOADBOARD',
  'DENIM',
  'SONAR',
  'RMIS',
];

export const REQUIREMENT_SETTINGS_SORT = [
  'MC_DOT_CHECKED',
  'DOCUMENTS_CHECKED',
  'OK_TO_LOAD',
  'DRIVER_CURRENT_LOCATION',
  'DRIVER_ESTIMATED_ARRIVAL',
  'TRAILER_NO',
  'FOUR_CHECK_CALLS',
  'LOCATION_MUST_HAVE_NAMES',
  'ASSIGN_SALES_PERSON_SHIPMENT',
  'MUST_DEFINE_AWARD_TYPE',
  'MUST_ENTER_COMPANY_INFO',
  'ASSIGN_LEAD_GENERATOR',
  'ASSIGN_PRICING_PERSON',
  'MUST_ENTER_CONTACT_INFO',
  'MUST_ENTER_LAST_NAME',
  'MUST_ENTER_ADDRESSES',
  'MUST_ENTER_PAYMENT_TERMS',
  'NON_MANAGER_EDITING_LOADS',
  'QUICKBOOKS_SEND_INVOICE_BEFORE_EXPORT',
  'CARGO_INSURANCE',
  'CARGO_INSURANCE_FINANCIALS',
  'AUTO_INSURANCE',
  'AUTO_INSURANCE_FINANCIALS',
  'GENERAL_INSURANCE',
  'GENERAL_INSURANCE_FINANCIALS',
  'APPOINTMENT_PERSON_REQUIRED',
  'BROKER_APPROVED_EQUIPMENT',
  'REVIEW_SHIPMENT_CARRIER',
  'CUSTOMERS_APPROVED_CREDIT',
  'USE_CUSTOM_SHIPMENT_LOCATION_ADDRESS',
  'SHIPMENT_CUSTOMER_PRICE',
  'SHIPMENT_STOP_DATES',
  'QUICKBOOKS_USE_DATA_FROM_TAX_INFORMATION',
  'UPDATE_STATUS_WHEN_CARRIER_INVOICE_IS_ADDED',
  'ADD_DELETE_THE_CARRIER_INVOICE_NO_IN_BILLS',
  'FOUR_CHECK_CALLS_BANYAN',
];

export interface PriceConfigExtended extends PriceConfig {
  // frontend fields
  category?: PRICE_CONFIGURATION_CATEGORY;
  adjustedValue?: string;
  type?: string;
  disabled?: boolean;
}

export interface PriceConfigDescription {
  id?: number;
  name: PRICE_CONFIGURATION_NAMES | string;
  description: PRICE_CONFIGURATION_DESCRIPTION;
  type: PRICE_CONFIGURATION_TYPE;
  valueType: PRICE_CONFIGURATION_VALUE_TYPE;
  parentName?: PRICE_CONFIGURATION_NAMES;
  category?: PRICE_CONFIGURATION_CATEGORY;
}
